import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import NotificationToast from '../components/notifications/NotificationToast';
import pushNotificationService from '../services/pushNotifications';
import notificationWebSocket from '../services/notificationWebSocket';

const NotificationContext = createContext(null);

// Default notification settings
const DEFAULT_SETTINGS = {
  enabled: true,           // Master switch for all notifications
  toastEnabled: true,      // Show toast notifications
  soundEnabled: true,      // Play sound on notification
  pushEnabled: false,      // Browser push notifications
  // Notification types (admin notifications always enabled, not configurable)
  tradeNotifications: true,    // TP/SL, position closed
  depositNotifications: true,  // Deposits approved/rejected
  withdrawalNotifications: true, // Withdrawals
  systemNotifications: true,   // System messages
};

// Load settings from localStorage
const loadSettings = () => {
  try {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error('Failed to load notification settings:', e);
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
const saveSettings = (settings) => {
  try {
    localStorage.setItem('notificationSettings', JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save notification settings:', e);
  }
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(loadSettings);
  const [pushPermission, setPushPermission] = useState(() => {
    // Initialize synchronously
    if (typeof window !== 'undefined' && pushNotificationService.isSupported()) {
      return pushNotificationService.getPermission();
    }
    return 'default';
  });
  const [wsConnected, setWsConnected] = useState(false);
  const unsubscribersRef = useRef([]);

  // Update setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      saveSettings(newSettings);
      return newSettings;
    });
  }, []);

  // Update multiple settings at once
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => {
      const merged = { ...prev, ...newSettings };
      saveSettings(merged);
      return merged;
    });
  }, []);

  // Toggle master switch
  const toggleNotifications = useCallback(() => {
    setSettings(prev => {
      const newSettings = { ...prev, enabled: !prev.enabled };
      saveSettings(newSettings);
      return newSettings;
    });
  }, []);

  // Request push notification permission
  const requestPushPermission = useCallback(async () => {
    const granted = await pushNotificationService.requestPermission();
    setPushPermission(pushNotificationService.getPermission());
    if (granted) {
      updateSetting('pushEnabled', true);
    }
    return granted;
  }, [updateSetting]);

  // Check if notification type is allowed
  const isNotificationTypeEnabled = useCallback((type) => {
    // Admin notifications always work - they cannot be disabled by user
    if (type === 'admin') return true;
    
    if (!settings.enabled) return false;
    
    switch (type) {
      case 'buy':
      case 'sell':
      case 'success': // position closed with profit
      case 'take_profit':
      case 'stop_loss':
        return settings.tradeNotifications;
      case 'deposit':
        return settings.depositNotifications;
      case 'withdrawal':
        return settings.withdrawalNotifications;
      case 'info':
      case 'system':
        return settings.systemNotifications;
      case 'error':
      case 'warning':
        return true; // Always show errors and warnings
      default:
        return true;
    }
  }, [settings]);

  const addNotification = useCallback((notification) => {
    // Check if notifications are enabled
    if (!settings.enabled || !settings.toastEnabled) {
      return null;
    }
    
    // Check if this notification type is enabled
    if (!isNotificationTypeEnabled(notification.type)) {
      return null;
    }

    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { ...notification, id }]);
    
    // Play sound if enabled
    if (settings.soundEnabled && notification.playSound !== false) {
      // Could add sound here
    }
    
    // Also show push notification if enabled and page is not focused
    if (settings.pushEnabled && document.hidden && notification.pushNotify !== false) {
      pushNotificationService.showCustom(
        notification.title, 
        notification.message || notification.details || '',
        notification.type
      );
    }
    
    return id;
  }, [settings, isNotificationTypeEnabled]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Connect WebSocket when user is logged in
  const connectWebSocket = useCallback((userId, token) => {
    if (!userId) return;
    
    // Disconnect existing connection
    notificationWebSocket.disconnect();
    
    // Connect with user ID
    notificationWebSocket.connect(userId, token);
    notificationWebSocket.startHeartbeat();
    
    // Subscribe to events
    const unsubConnect = notificationWebSocket.on('connected', () => {
      setWsConnected(true);
      console.log('🔔 WebSocket connected for notifications');
    });

    const unsubDisconnect = notificationWebSocket.on('disconnected', () => {
      setWsConnected(false);
    });

    // Handle incoming notifications
    const unsubNotification = notificationWebSocket.on('notification', (payload) => {
      addNotification({
        type: payload.type || 'info',
        title: payload.title || 'Уведомление',
        message: payload.message,
        ...payload
      });
    });

    // Deposit notifications
    const unsubDeposit = notificationWebSocket.on('deposit', (payload) => {
      addNotification({
        type: payload.status === 'approved' ? 'deposit' : 'error',
        title: payload.status === 'approved' ? 'Депозит одобрен' : 'Депозит отклонён',
        message: payload.status === 'approved' 
          ? `+${payload.amount} ${payload.currency || 'USDT'} зачислено на баланс`
          : payload.reason || 'Депозит отклонён администратором',
      });
    });

    // Withdrawal notifications
    const unsubWithdrawal = notificationWebSocket.on('withdrawal', (payload) => {
      addNotification({
        type: payload.status === 'approved' ? 'withdrawal' : 'error',
        title: payload.status === 'approved' ? 'Вывод обработан' : 'Вывод отклонён',
        message: payload.status === 'approved'
          ? `-${payload.amount} ${payload.currency || 'USDT'} отправлено`
          : payload.reason || 'Вывод отклонён администратором',
      });
    });

    // Position closed
    const unsubPositionClosed = notificationWebSocket.on('position_closed', (payload) => {
      const pnl = payload.pnl || 0;
      addNotification({
        type: pnl >= 0 ? 'success' : 'error',
        title: `Позиция закрыта: ${payload.symbol}`,
        message: pnl >= 0 ? `Прибыль: +$${pnl.toFixed(2)}` : `Убыток: -$${Math.abs(pnl).toFixed(2)}`,
      });
    });

    // Take Profit hit
    const unsubTakeProfit = notificationWebSocket.on('take_profit', (payload) => {
      addNotification({
        type: 'success',
        title: `🎯 Take Profit: ${payload.symbol}`,
        message: `Достигнута цель! PnL: +$${(payload.pnl || 0).toFixed(2)}`,
      });
    });

    // Stop Loss hit
    const unsubStopLoss = notificationWebSocket.on('stop_loss', (payload) => {
      addNotification({
        type: 'warning',
        title: `⚠️ Stop Loss: ${payload.symbol}`,
        message: `Сработал SL. PnL: -$${Math.abs(payload.pnl || 0).toFixed(2)}`,
      });
    });

    // Liquidation
    const unsubLiquidation = notificationWebSocket.on('liquidation', (payload) => {
      addNotification({
        type: 'error',
        title: `💥 Ликвидация: ${payload.symbol}`,
        message: payload.message || 'Позиция ликвидирована',
      });
    });

    // Balance update
    const unsubBalance = notificationWebSocket.on('balance_update', (payload) => {
      addNotification({
        type: 'info',
        title: 'Баланс обновлён',
        message: `Новый баланс: $${(payload.balance || 0).toFixed(2)}`,
      });
    });

    // Announcement
    const unsubAnnouncement = notificationWebSocket.on('announcement', (payload) => {
      addNotification({
        type: 'info',
        title: payload.title || '📢 Объявление',
        message: payload.message,
      });
    });

    // Store unsubscribers for cleanup
    unsubscribersRef.current = [
      unsubConnect, unsubDisconnect, unsubNotification, unsubDeposit,
      unsubWithdrawal, unsubPositionClosed, unsubTakeProfit, unsubStopLoss,
      unsubLiquidation, unsubBalance, unsubAnnouncement
    ];
  }, [addNotification]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    // Unsubscribe from all events
    unsubscribersRef.current.forEach(unsub => unsub && unsub());
    unsubscribersRef.current = [];
    
    notificationWebSocket.stopHeartbeat();
    notificationWebSocket.disconnect();
    setWsConnected(false);
  }, []);

  // Convenience methods
  const toast = {
    // Generic success
    success: (title, message, options = {}) => addNotification({
      type: 'success',
      title,
      message,
      ...options
    }),

    // Generic error
    error: (title, message, options = {}) => addNotification({
      type: 'error',
      title,
      message,
      ...options
    }),

    // Generic warning
    warning: (title, message, options = {}) => addNotification({
      type: 'warning',
      title,
      message,
      ...options
    }),

    // Generic info
    info: (title, message, options = {}) => addNotification({
      type: 'info',
      title,
      message,
      ...options
    }),

    // Trade buy success
    buy: (asset, amount, price, options = {}) => addNotification({
      type: 'buy',
      title: `Успешная покупка ${asset}!`,
      message: `Куплено на $${amount.toFixed(2)}`,
      details: `Цена: $${price.toFixed(2)}`,
      ...options
    }),

    // Trade sell success
    sell: (asset, quantity, pnl, options = {}) => addNotification({
      type: 'sell',
      title: `Успешная продажа ${asset}!`,
      message: `Продано ${quantity.toFixed(6)} ${asset}`,
      details: pnl >= 0 ? `P&L: +$${pnl.toFixed(2)} ✓` : `P&L: -$${Math.abs(pnl).toFixed(2)}`,
      ...options
    }),

    // Futures position opened
    positionOpened: (symbol, side, leverage, margin, options = {}) => addNotification({
      type: side === 'long' ? 'buy' : 'sell',
      title: `Позиция открыта: ${symbol}`,
      message: `${side.toUpperCase()} ${leverage}x`,
      details: `Маржа: $${margin.toFixed(2)}`,
      ...options
    }),

    // Futures position closed
    positionClosed: (symbol, pnl, options = {}) => addNotification({
      type: pnl >= 0 ? 'success' : 'error',
      title: `Позиция закрыта: ${symbol}`,
      message: pnl >= 0 ? `Прибыль: +$${pnl.toFixed(2)}` : `Убыток: -$${Math.abs(pnl).toFixed(2)}`,
      ...options
    }),

    // Take Profit triggered
    takeProfit: (symbol, pnl, options = {}) => addNotification({
      type: 'take_profit',
      title: `Take Profit: ${symbol}`,
      message: `Достигнута цель! +$${pnl.toFixed(2)}`,
      ...options
    }),

    // Stop Loss triggered
    stopLoss: (symbol, pnl, options = {}) => addNotification({
      type: 'stop_loss',
      title: `Stop Loss: ${symbol}`,
      message: `Сработал стоп-лосс. -$${Math.abs(pnl).toFixed(2)}`,
      ...options
    }),

    // Liquidation
    liquidation: (symbol, loss, options = {}) => addNotification({
      type: 'liquidation',
      title: `Ликвидация: ${symbol}`,
      message: `Позиция ликвидирована. Убыток: $${Math.abs(loss).toFixed(2)}`,
      duration: 8000, // Longer duration for important notice
      ...options
    }),

    // Admin notification (always shown)
    admin: (title, message, options = {}) => addNotification({
      type: 'admin',
      title,
      message,
      ...options
    }),

    // Promo notification
    promo: (title, message, options = {}) => addNotification({
      type: 'promo',
      title,
      message,
      ...options
    }),

    // Limit order placed
    limitOrder: (symbol, side, price, options = {}) => addNotification({
      type: 'info',
      title: 'Лимитный ордер размещён',
      message: `${symbol} ${side.toUpperCase()} @ $${price.toFixed(2)}`,
      ...options
    }),

    // Deposit
    deposit: (amount, currency = 'USDT', options = {}) => addNotification({
      type: 'deposit',
      title: 'Депозит одобрен',
      message: `+${amount.toFixed(2)} ${currency}`,
      ...options
    }),

    // Withdrawal
    withdrawal: (amount, currency = 'USDT', options = {}) => addNotification({
      type: 'withdrawal',
      title: 'Вывод обработан',
      message: `-${amount.toFixed(2)} ${currency}`,
      ...options
    }),

    // Custom notification
    custom: (notification) => addNotification(notification)
  };

  const value = {
    notifications,
    addNotification,
    removeNotification,
    toast,
    // Notification settings
    settings,
    updateSetting,
    updateSettings,
    toggleNotifications,
    isNotificationTypeEnabled,
    // Push notification controls
    pushEnabled: settings.pushEnabled,
    pushPermission,
    requestPushPermission,
    pushService: pushNotificationService,
    // WebSocket controls
    wsConnected,
    connectWebSocket,
    disconnectWebSocket,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {settings.toastEnabled && (
        <NotificationToast 
          notifications={notifications} 
          removeNotification={removeNotification} 
        />
      )}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
